import type { DragEndEvent } from '@dnd-kit/core';
import { LINKS } from '@fans-fund-me/shared-comm-react-cosmos/fans-fund-me/links';
import { Screen } from '@fans-fund-me/shared-comm-react-cosmos/fans-fund-me/profile-screen';
import type {
  ProfileLink,
  Profile as ProfileType,
  Wishlist,
  WishlistItem,
} from '@fans-fund-me/shared-comm-react-cosmos/fans-fund-me/types';
import { useAuth } from '@fans-fund-me/shared-common-esm-authentication';
import { useCurrency } from '@fans-fund-me/shared-common-esm-currency';
import { useModalsStack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useFileDialog } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { arrayMoveImmutable } from 'array-move';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { createSurpriseGiftPayment, updateSurpriseGiftPayment } from '../../api/payments.ts';
import {
  addLink,
  addWishlistItem,
  deleteLink,
  deleteWishlistItem,
  getProfileByUsername,
  getWishlistByUsername,
  updateLink,
  updateProfile,
  updateWishlistItem,
} from '../../api/profile.ts';
import { FORM_ERRORS } from '../../constants/form_errors.ts';

function Profile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const currency = useCurrency();

  const [profileId, setProfileId] = useState('');

  const [profile, setProfile] = useState<ProfileType>({
    privileges: [],
    bio: '',
    coverImage: '',
    currency: '',
    displayName: '',
    isAcceptingPayments: false,
    isSurpriseGiftEnabled: false,
    links: [],
    profilePicture: '',
    surpriseGiftMinimumAmount: undefined,
    isLoading: true,
    isErrored: false,
    isEditable: false,
  });
  const [wishlist, setWishlist] = useState<Wishlist>({
    isLoading: true,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    items: new Array(8).fill().map((_, i) => ({ id: `loading-wishlist-item-${i}` })),
    isErrored: false,
  });
  const fileDialog = useFileDialog({ multiple: false, accept: 'image/*' });
  const modalStack = useModalsStack([
    'wishlist-item-form',
    'profile-form',
    'crop-image-form',
    'profile-link-form',
    'surprise-gift-form',
  ]);
  const [isLoading, setIsLoading] = useState<
    'wishlist-item-form' | 'profile-form' | 'crop-image-form' | 'profile-link-form' | 'surprise-gift-form' | false
  >(false);
  const [image, setImage] = useState<File | null>(null);
  const [surpriseGiftId, setSurpriseGiftId] = useState('');
  const [surpriseGiftExchangeRate, setSurpriseGiftExchangeRate] = useState('');
  const [surpriseGiftConvertedAmount, setSurpriseGiftConvertedAmount] = useState('');

  const blobToBase64 = (blob: Blob) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise((resolve) => {
      reader.onloadend = () => {
        resolve((reader.result as string).split('base64,')[1]);
      };
    });
  };

  const handleDragEndWishlist: (event: DragEndEvent) => void = (event: DragEndEvent) => {
    const { active, over } = event;
    const newWishlist = { ...wishlist };

    if (active.id !== over?.id) {
      const oldIndex = newWishlist.items.map((e) => e.id).indexOf(String(active.id));
      const newIndex = newWishlist.items.map((e) => e.id).indexOf(String(over?.id));

      // Update the wishlist items order
      newWishlist.items = arrayMoveImmutable(newWishlist.items, oldIndex, newIndex);

      setWishlist(newWishlist);
      updateWishlistItem(
        {
          index: newIndex,
        },
        auth.id,
        active.id.toString(),
        auth.token,
      );
    }
  };

  const handleDragEndLinks: (event: DragEndEvent) => void = (event: DragEndEvent) => {
    const { active, over } = event;
    const newProfile = { ...profile };

    if (active.id !== over?.id) {
      const oldIndex = newProfile.links.map((e) => e.id).indexOf(String(active.id));
      const newIndex = newProfile.links.map((e) => e.id).indexOf(String(over?.id));

      // Update the profile links order
      newProfile.links = arrayMoveImmutable(newProfile.links, oldIndex, newIndex);
      setProfile(newProfile);

      updateLink(
        {
          index: newIndex,
        },
        auth.id,
        active.id.toString(),
        auth.token,
      );
    }
  };

  const handleClickEditProfileLink = (link: ProfileLink) => {
    if (profile.isEditable) {
      profileLinkForm.reset();
      profileLinkForm.setValues({
        id: link.id,
        label: link.label,
        url: link.url,
      });

      modalStack.open('profile-link-form');
    } else {
      window.open(link.url, '_blank');
    }
  };

  interface wishlistFormValues {
    id: string;
    image: Blob | string;
    name: string;
    price: string;
    repeat_purchases: boolean;
    delete: boolean;
  }

  const wishlistForm = useForm<wishlistFormValues>({
    mode: 'uncontrolled',
    initialValues: {
      id: '',
      image: '',
      name: '',
      price: '',
      repeat_purchases: false,
      delete: false,
    },
    validate: {
      name: (value) => (value.length < 2 ? FORM_ERRORS.NAME_LENGTH_VALIDATION : null),
      price: (value) =>
        value === ''
          ? FORM_ERRORS.PRICE_REQUIRED
          : !new RegExp('^\\d{1,8}(\\.\\d{1,2})?$').test(value)
            ? FORM_ERRORS.MUST_BE_CURRENCY
            : null,
    },
  });

  const profileLinkForm = useForm({
    mode: 'uncontrolled',
    initialValues: {
      id: '',
      label: '',
      url: '',
      delete: false,
    },
    validate: {
      url: (value) => (!value.includes('https://') ? FORM_ERRORS.VALID_URL : null),
      label: (value) => (value.length < 2 ? FORM_ERRORS.LABEL_LENGTH_VALIDATION : null),
    },
  });

  interface profileFormValues {
    cover_image: Blob | string;
    profile_picture: Blob | string;
    display_name: string;
    bio: string;
  }

  const profileForm = useForm<profileFormValues>({
    mode: 'uncontrolled',
    initialValues: {
      cover_image: '',
      profile_picture: '',
      display_name: '',
      bio: '',
    },
    validate: {
      display_name: (value) => (value.length < 2 ? FORM_ERRORS.DISPLAY_NAME_LENGTH_VALIDATION : null),
      // bio: (value) => (value.length < 2 ? FORM_ERRORS.BIO_LENGTH_VALIDATION : null),
    },
  });

  const surpriseGiftForm = useForm({
    mode: 'uncontrolled',
    initialValues: {
      amount: '',
      reason: '',
    },
    validate: {
      amount: (value) =>
        value === ''
          ? FORM_ERRORS.AMOUNT_REQUIRED
          : !new RegExp('^\\d{1,8}(\\.\\d{1,2})?$').test(value)
            ? FORM_ERRORS.MUST_BE_CURRENCY
            : null,
    },
  });

  const handleProfileFormSubmit = async (values: typeof profileForm.values) => {
    setIsLoading('profile-form');
    if (
      typeof values.cover_image !== 'string' &&
      values.cover_image !== null &&
      values.cover_image?.size > 5 * 1024 * 1024
    ) {
      profileForm.setErrors({
        cover_image: FORM_ERRORS.IMAGE_TOO_BIG,
      });
      setIsLoading(false);
      return;
    }

    if (
      typeof values.profile_picture !== 'string' &&
      values.profile_picture !== null &&
      values.profile_picture?.size > 5 * 1024 * 1024
    ) {
      profileForm.setErrors({
        profile_picture: FORM_ERRORS.IMAGE_TOO_BIG,
      });
      setIsLoading(false);
      return;
    }

    updateProfile(
      Object.assign({
        ...(typeof values.cover_image !== 'string' &&
          values.cover_image !== null && {
            coverImage: await blobToBase64(values.cover_image),
          }),
        ...(typeof values.profile_picture !== 'string' &&
          values.profile_picture !== null && { profilePic: await blobToBase64(values.profile_picture) }),
        displayName: values.display_name,
        bio: values.bio,
      }),
      auth.id,
      auth.token,
    )
      .then((response) => {
        const newProfile = { ...profile };
        newProfile.coverImage = response.data.coverImage;
        newProfile.profilePicture = response.data.profilePic;
        newProfile.displayName = response.data.displayName;
        newProfile.bio = response.data.bio;
        setProfile(newProfile);
        modalStack.close('profile-form');
        profileForm.reset();
        setIsLoading(false);
      })
      .catch((error) => {
        profileForm.setErrors({
          cover_image: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'coverImage',
          )?.message,
          profile_picture: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'profilePic',
          )?.message,
          display_name: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'displayName',
          )?.message,
          bio: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'bio',
          )?.message,
        });
        setIsLoading(false);
      });
  };

  const handleWishlistItemFormSubmit = async (values: typeof wishlistForm.values) => {
    setIsLoading('wishlist-item-form');
    if (typeof values.image !== 'string' && values.image !== null && values.image.size > 5 * 1024 * 1024) {
      wishlistForm.setErrors({
        image: FORM_ERRORS.IMAGE_TOO_BIG,
      });
      setIsLoading(false);
      return;
    }

    if (values.delete) {
      deleteWishlistItem(auth.id, values.id, auth.token).then(() => {
        const newWishlist = { ...wishlist };
        newWishlist.items = newWishlist.items.filter((item) => item.id !== values.id);
        setWishlist(newWishlist);
        modalStack.close('wishlist-item-form');
        wishlistForm.reset();
        setIsLoading(false);
      });
    } else if (values.id) {
      updateWishlistItem(
        Object.assign({
          ...(typeof values.image !== 'string' && values.image !== null && { image: await blobToBase64(values.image) }),
          id: values.id,
          name: values.name,
          price: values.price,
          allowMultiplePurchases: values.repeat_purchases,
        }),
        auth.id,
        values.id,
        auth.token,
      )
        .then((response) => {
          // const newWishlist = { ...wishlist };
          // const itemIndex = newWishlist.items.findIndex((item) => item.id === values.id);
          // if (itemIndex !== -1) {
          //   newWishlist.items[itemIndex] = {
          //     id: response.data.id,
          //     image: response.data.image,
          //     name: response.data.name,
          //     price: response.data.price,
          //     allowMultiplePurchases: response.data.allowMultiplePurchases,
          //   };
          // }
          const newWishlist = { ...wishlist };
          newWishlist.items = response.data.items;
          setWishlist(newWishlist);
          modalStack.close('wishlist-item-form');
          wishlistForm.reset();
          setIsLoading(false);
          return;
        })
        .catch((error) => {
          wishlistForm.setErrors({
            image: error.response.data.fields?.find(
              (field: { fieldName: string; message: string }) => field.fieldName === 'image',
            )?.message,
            name: error.response.data.fields?.find(
              (field: { fieldName: string; message: string }) => field.fieldName === 'name',
            )?.message,
            price: error.response.data.fields?.find(
              (field: { fieldName: string; message: string }) => field.fieldName === 'price',
            )?.message,
          });
          setIsLoading(false);
        });
    } else {
      addWishlistItem(
        Object.assign({
          ...(typeof values.image !== 'string' && values.image !== null && { image: await blobToBase64(values.image) }),
          name: values.name,
          price: values.price,
          allowMultiplePurchases: values.repeat_purchases,
        }),
        auth.id,
        auth.token,
      )
        .then((response) => {
          const newWishlist = { ...wishlist };
          newWishlist.items.push({
            id: response.data.id,
            image: response.data.image,
            name: response.data.name,
            price: response.data.price,
            formattedPrice: response.data.formattedPrice,
            allowMultiplePurchases: response.data.allowMultiplePurchases,
          });
          setWishlist(newWishlist);
          setImage(null);
          modalStack.close('wishlist-item-form');
          setIsLoading(false);
          wishlistForm.reset();
        })
        .catch((error) => {
          wishlistForm.setErrors({
            image: error.response.data.fields?.find(
              (field: { fieldName: string; message: string }) => field.fieldName === 'image',
            )?.message,
            name: error.response.data.fields?.find(
              (field: { fieldName: string; message: string }) => field.fieldName === 'name',
            )?.message,
            price: error.response.data.fields?.find(
              (field: { fieldName: string; message: string }) => field.fieldName === 'price',
            )?.message,
          });
          setIsLoading(false);
        });
    }
  };

  const handleProfileLinkFormSubmit = (values: typeof profileLinkForm.values) => {
    setIsLoading('profile-link-form');
    if (values.delete) {
      deleteLink(auth.id, values.id, auth.token).then(() => {
        const newProfile = { ...profile };
        newProfile.links = newProfile.links.filter((link) => link.id !== values.id);
        setProfile(newProfile);
        modalStack.close('profile-link-form');
        profileLinkForm.reset();
        setIsLoading(false);
      });
    } else if (values.id) {
      updateLink(
        {
          label: values.label,
          url: values.url,
        },
        auth.id,
        values.id,
        auth.token,
      )
        .then((response) => {
          const newProfile = { ...profile };
          newProfile.links = response.data;
          setProfile(newProfile);
          modalStack.close('profile-link-form');
          profileLinkForm.reset();
          setIsLoading(false);
          return;
        })
        .catch((error) => {
          profileLinkForm.setErrors({
            label: error.response.data.fields?.find(
              (field: { fieldName: string; message: string }) => field.fieldName === 'label',
            )?.message,
            url: error.response.data.fields?.find(
              (field: { fieldName: string; message: string }) => field.fieldName === 'url',
            )?.message,
          });
          setIsLoading(false);
        });
    } else {
      addLink(
        {
          label: values.label,
          url: values.url,
        },
        auth.id,
        auth.token,
      )
        .then((response) => {
          const newProfile = { ...profile };
          newProfile.links.push({
            id: response.data.id,
            url: response.data.url,
            label: response.data.label,
          });
          setProfile(newProfile);
          modalStack.close('profile-link-form');
          setIsLoading(false);
          wishlistForm.reset();
        })
        .catch((error) => {
          profileLinkForm.setErrors({
            label: error.response.data.fields?.find(
              (field: { fieldName: string; message: string }) => field.fieldName === 'label',
            )?.message,
            url: error.response.data.fields?.find(
              (field: { fieldName: string; message: string }) => field.fieldName === 'url',
            )?.message,
          });
          setIsLoading(false);
        });
    }
  };

  const handleClickWishlistItem = (item: WishlistItem) => {
    wishlistForm.reset();
    wishlistForm.setValues({
      id: item.id,
      image: item.image,
      name: item.name,
      price: item.price,
      repeat_purchases: item.allowMultiplePurchases,
    });

    modalStack.open('wishlist-item-form');
  };

  const handleCheckout = (item: WishlistItem) => {
    localStorage.removeItem('ffm_checkout_user');
    localStorage.removeItem('ffm_checkout_username');
    localStorage.removeItem('ffm_checkout_item');
    localStorage.removeItem('ffm_checkout_surprise_gift');
    localStorage.removeItem('ffm_checkout_surprise_gift_amount');
    localStorage.removeItem('ffm_checkout_surprise_gift_id');
    localStorage.removeItem('ffm_checkout_surprise_gift_message');

    localStorage.setItem('ffm_checkout_user', profileId);
    localStorage.setItem('ffm_checkout_username', username!);
    localStorage.setItem('ffm_checkout_item', item.id);
    navigate(LINKS.CHECKOUT);
  };

  const handleSurpriseGiftFormSubmit = (values: typeof surpriseGiftForm.values) => {
    setIsLoading('surprise-gift-form');
    updateSurpriseGiftPayment(
      { id: surpriseGiftId, suggestion: values.reason, senderAmount: values.amount },
      surpriseGiftId,
      auth.token,
    )
      .then((response) => {
        const paymentId = response.data.singlePaymentId;

        localStorage.removeItem('ffm_checkout_user');
        localStorage.removeItem('ffm_checkout_item');
        localStorage.removeItem('ffm_checkout_surprise_gift');
        localStorage.removeItem('ffm_checkout_surprise_gift_amount');
        localStorage.removeItem('ffm_checkout_surprise_gift_id');
        localStorage.removeItem('ffm_checkout_surprise_gift_payment_id');
        localStorage.removeItem('ffm_checkout_surprise_gift_message');

        localStorage.setItem('ffm_checkout_user', profileId);
        localStorage.setItem('ffm_checkout_username', username!);
        localStorage.setItem('ffm_checkout_surprise_gift', 'true');
        localStorage.setItem('ffm_checkout_surprise_gift_id', surpriseGiftId);
        localStorage.setItem('ffm_checkout_surprise_gift_payment_id', paymentId);
        localStorage.setItem('ffm_checkout_surprise_gift_amount', values.amount);
        localStorage.setItem('ffm_checkout_surprise_gift_message', values.reason);
        modalStack.close('surprise-gift-form');
        surpriseGiftForm.reset();
        setIsLoading(false);
        navigate('/checkout');
      })
      .catch((error) => {
        surpriseGiftForm.setErrors({
          amount: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'senderAmount',
          )?.message,
        });
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (fileDialog.files && fileDialog.files.length > 0) {
      const imageFile = fileDialog.files[0];
      if (imageFile && profile.isEditable && !modalStack.state['crop-image-form'] && imageFile !== image) {
        modalStack.open('crop-image-form');
        if (imageFile != image) {
          setImage(imageFile);
          fileDialog.reset();
        }
      }
    }
  }, [fileDialog.files, modalStack.state, image, profile.isEditable, fileDialog, modalStack]);

  useEffect(() => {
    if (modalStack.state['profile-form'] && !profileForm.isDirty()) {
      profileForm.reset();
      profileForm.setValues({
        cover_image: profile.coverImage,
        profile_picture: profile.profilePicture,
        display_name: profile.displayName,
        bio: profile.bio,
      });
    }
    if (modalStack.state['surprise-gift-form']) {
      createSurpriseGiftPayment(
        {
          receiverId: profileId,
          currency: currency.value,
        },
        auth.token,
      )
        .then((response) => {
          setSurpriseGiftId(response.data.id);
          setSurpriseGiftExchangeRate(response.data.exchangeRate);
        })
        .catch(() => {
          notifications.show({
            title: 'Error Creating Surprise Gift',
            message: "It wasn't possible to initiate your Surprise Gift. Please try again later.",
            autoClose: 7000,
            color: 'red',
          });
          modalStack.close('surprise-gift-form');
        });
    }
    if (!modalStack.state['profile-form']) {
      profileForm.resetDirty();
    }
    if (!modalStack.state['wishlist-item-form']) {
      wishlistForm.reset();
    }
    if (!modalStack.state['profile-link-form']) {
      profileLinkForm.reset();
    }
    if (!modalStack.state['surprise-gift-form']) {
      surpriseGiftForm.reset();
    }
  }, [
    auth.token,
    currency.value,
    modalStack.state,
    profileId,
    profile.bio,
    profile.coverImage,
    profile.displayName,
    profile.profilePicture,
  ]);

  surpriseGiftForm.watch('amount', ({ value }) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: profile.currency,
    });
    const convertedAmount = Number(surpriseGiftExchangeRate) * Number(value || 0);
    setSurpriseGiftConvertedAmount(formatter.format(convertedAmount));
  });

  useEffect(() => {
    if (currency.value !== null && currency.value !== '' && currency.value !== undefined) {
      getProfileByUsername(username!, auth.token)
        .then((response) => {
          localStorage.removeItem('ffm_checkout_user');

          setProfileId(response.data.userId);

          setProfile({
            isLoading: false,
            isErrored: false,
            privileges: response.data.privileges,
            isAcceptingPayments: response.data.acceptingPayments,
            isSurpriseGiftEnabled: response.data.enableSurpriseGifts,
            isEditable: auth.id === response.data.userId,
            surpriseGiftMinimumAmount: response.data.surpriseGiftsMinimum,
            coverImage: response.data.coverImage,
            profilePicture: response.data.profilePic,
            displayName: response.data.displayName,
            bio: response.data.bio,
            links: response.data.links,
            currency: response.data.currency,
          });
        })
        .catch((error) => {
          if (error?.response?.status === 404) {
            navigate(LINKS.NOT_FOUND);
          } else if (error.code === 'ECONNABORTED') {
            navigate(LINKS.SERVER_ERROR);
          } else if (error.code === 'ERR_NETWORK') {
            navigate(LINKS.SERVER_ERROR);
          } else {
            setProfile({
              isLoading: false,
              isErrored: true,
              privileges: [],
              bio: '',
              coverImage: '',
              currency: '',
              displayName: '',
              isAcceptingPayments: false,
              isEditable: false,
              isSurpriseGiftEnabled: false,
              links: [],
              profilePicture: '',
              surpriseGiftMinimumAmount: undefined,
            });
          }
        });
      getWishlistByUsername(username!, currency.value, auth.token)
        .then((response) => {
          setWishlist({
            isLoading: false,
            isErrored: false,
            items: response.data.items,
          });
        })
        .catch(() => {
          setWishlist({
            isLoading: false,
            isErrored: true,
            items: [],
          });
        });
    }
  }, [auth.id, auth.token, currency.value, navigate, username]);

  return (
    <Screen
      profile={profile}
      wishlist={wishlist}
      modalStack={modalStack}
      isLoading={isLoading}
      fileDialog={fileDialog}
      wishlistItemForm={wishlistForm}
      handleWishlistItemFormSubmit={handleWishlistItemFormSubmit}
      profileLinkForm={profileLinkForm}
      handleProfileLinkFormSubmit={handleProfileLinkFormSubmit}
      profileForm={profileForm}
      handleProfileFormSubmit={handleProfileFormSubmit}
      surpriseGiftForm={surpriseGiftForm}
      handleSurpriseGiftFormSubmit={handleSurpriseGiftFormSubmit}
      convertedSurpriseGiftAmount={surpriseGiftConvertedAmount}
      handleClickWishlistItem={profile.isEditable ? handleClickWishlistItem : handleCheckout}
      handleDragEndWishlist={handleDragEndWishlist}
      handleDragEndLinks={handleDragEndLinks}
      handleClickEditProfileLink={handleClickEditProfileLink}
      stencilHeight={100}
      stencilWidth={100}
      defaultCoverImage={`https://fans-fund-me-core-profile-user-images-prod.s3.eu-west-2.amazonaws.com/cover_image/FFM-default-cover-image.png`}
      defaultProfilePicture={`https://fans-fund-me-core-profile-user-images-prod.s3.eu-west-2.amazonaws.com/profile_pic/FFM-default-profile-picture.png`}
      defaultWishlistItem={`https://fans-fund-me-core-profile-user-images-prod.s3.eu-west-2.amazonaws.com/wishlist/FFM-default-wishlist-item.jpg`}
    />
  );
}

export default Profile;
